import pick from 'lodash/pick';
import queryString from 'query-string';
import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { trackIntercomEvent, updateIntercomUser } from '../../util/intercom';
import { buildAuthorPublicData } from '../../util/listingAuthorData';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { transactionLineItems, markTxUnread, fetchOffPlatformReviews } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { TRANSITION_ENQUIRE } from '../../util/transaction';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { publicFileAttachmentsMaybe } from '../../ducks/messageAttachments.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const QUERY_OFF_PLATFORM_REVIEWS_REQUEST =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_REQUEST';
export const QUERY_OFF_PLATFORM_REVIEWS_SUCCESS =
  'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_SUCCESS';
export const QUERY_OFF_PLATFORM_REVIEWS_ERROR = 'app/ProfilePage/QUERY_OFF_PLATFORM_REVIEWS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ListingPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ListingPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ListingPage/SEND_ENQUIRY_ERROR';

export const PUBLISH_DRAFT_REQUEST = 'app/ListingPage/PUBLISH_DRAFT_REQUEST';
export const PUBLISH_DRAFT_SUCCESS = 'app/ListingPage/PUBLISH_DRAFT_SUCCESS';
export const PUBLISH_DRAFT_ERROR = 'app/ListingPage/PUBLISH_DRAFT_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  offPlatformReviews: [],
  queryOffPlatformReviewsError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendEnquiryInProgress: false,
  sendEnquiryError: null,
  enquiryModalOpenForListingId: null,
  enquiryPackage: null,
  pricingPackage: null,
  suggestedPrice: null,
  userListingRefs: [],
  publishInProgress: false,
  publishError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case QUERY_OFF_PLATFORM_REVIEWS_REQUEST:
      return { ...state, queryOffPlatformReviewsError: null };
    case QUERY_OFF_PLATFORM_REVIEWS_SUCCESS:
      return { ...state, offPlatformReviews: payload };
    case QUERY_OFF_PLATFORM_REVIEWS_ERROR:
      return { ...state, offPlatformReviews: [], queryOffPlatformReviewsError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };

    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryInProgress: false };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };

    case PUBLISH_DRAFT_REQUEST:
      return { ...state, publishInProgress: true, publishError: null };
    case PUBLISH_DRAFT_SUCCESS:
      return { ...state, publishInProgress: false };
    case PUBLISH_DRAFT_ERROR:
      return { ...state, publishInProgress: false, publishError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryOffPlatformReviewsRequest = () => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_REQUEST,
});

export const queryOffPlatformReviewsSuccess = reviews => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryOffPlatformReviewsError = e => ({
  type: QUERY_OFF_PLATFORM_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = () => ({ type: SEND_ENQUIRY_SUCCESS });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });

export const publishDraftRequest = () => ({ type: PUBLISH_DRAFT_REQUEST });
export const publishDraftSuccess = () => ({ type: PUBLISH_DRAFT_SUCCESS });
export const publishDraftError = e => ({ type: PUBLISH_DRAFT_ERROR, error: true, payload: e });

// ================ Thunks ================ //

// `authorId` is given for the owner's own drafts: sdk.listings.show can't
// read a draft, so the author can't be looked up from the listing.
export const queryUserListings = (listingId, authorId = null) => async (
  dispatch,
  getState,
  sdk
) => {
  let userId = authorId;
  if (!userId) {
    const user = await sdk.listings.show({ id: listingId, include: ['author'] });
    userId = user.data.data.relationships.author.data.id.uuid;
  }
  dispatch(queryListingsRequest(userId));
  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'fields.image': [
        'variants.landscape-crop',
        'variants.landscape-crop2x',
        'variants.square-small2x',
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',
      ],
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const tempListingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      const listingRefs = tempListingRefs.filter(listing => listing.id.uuid !== listingId.uuid);

      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const publishListingDraft = (listingId, currentUser) => (dispatch, getState, sdk) => {
  dispatch(publishDraftRequest());

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(publishDraftSuccess());
      if (typeof window === 'object') {
        updateIntercomUser({ listingCreated: true });
      }
      return response.data.data;
    })
    .then(publishedListing => {
      // Fire and forget: the listing is already published.
      sdk.ownListings
        .update({
          id: new UUID(publishedListing.id.uuid),
          publicData: buildAuthorPublicData(currentUser),
        })
        .catch(e =>
          log.error(e, 'publish-author-data-update-failed', { listingId: listingId.uuid })
        );
      return publishedListing;
    })
    .catch(e => {
      dispatch(publishDraftError(storableError(e)));
    });
};

export const showListing = (listingId, isOwn = false, searchParams) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Image carousel
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      dispatch(addMarketplaceEntities(data));
      return data;
    })
    .then(response => {
      const listings = getListingsById(getState(), [getState().ListingPage.id]);

      if (Array.isArray(listings) && listings.length > 0) {
        const listing = listings[0];
        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'view_item',
            content_type: 'service',
            ecommerce: {
              items: [
                {
                  item_name: listing.attributes.title,
                  item_id: listing.id.uuid,
                  price: listing.attributes.publicData?.priceArray
                    ? '' + listing.attributes.publicData?.priceArray[0]?._packagePrice / 100
                    : '' + listing.attributes.price.amount / 100,
                  item_brand: listing.author.attributes.profile.displayName,
                  item_category: listing.attributes.publicData.category,
                  quantity: '1',
                },
              ],
            },
          });
        }
      }

      return response;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchReviews = listingId => async (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());

  sdk.listings
    .show({ id: listingId, include: ['author'] })
    .then(listingResponse => {
      const authorId = listingResponse.data.data.relationships.author.data.id;

      return sdk.reviews
        .query({
          subject_id: authorId,
          state: 'public',
          include: ['author', 'author.profileImage'],
          'fields.image': [
            'variants.square-small',
            'variants.square-small2x',
            'variants.scaled-small',
            'variants.scaled-medium',
            'variants.scaled-large',
            'variants.scaled-xlarge',
          ],
        })
        .then(reviewsResponse => {
          const reviews = denormalisedResponseEntities(reviewsResponse);
          dispatch(fetchReviewsSuccess(reviews));
          return authorId;
        });
    })
    .then(authorId =>
      fetchOffPlatformReviews(authorId.uuid).then(offPlatformReviews => {
        dispatch(queryOffPlatformReviewsSuccess(offPlatformReviews.items));
      })
    )
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const sendEnquiry = (
  listingId,
  isLightEntrepreneur,
  message,
  currentUser,
  listingTitle,
  fileIds = [],
  enquiryPackage = null
) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());

  // Every enquiry starts on default or light; invoicing is a branch inside
  // those processes (spec 2026-09-22), so the customer's flags play no part.
  const processAlias = isLightEntrepreneur ? config.lightProcessAlias : config.bookingProcessAlias;

  const bodyParams = {
    transition: TRANSITION_ENQUIRE,
    processAlias: processAlias,
    params: {
      listingId,
      protectedData: { listingTitle, ...(enquiryPackage ? { enquiryPackage } : {}) },
    },
  };
  return sdk.transactions
    .initiate(bodyParams, { expand: true, include: ['provider'] })
    .then(response => {
      const transaction = response.data.data;
      const transactionId = transaction.id;
      const provider = transaction.relationships.provider.data;
      const recipientId = provider.id;

      return sdk.messages
        .send(
          { transactionId, content: message, ...publicFileAttachmentsMaybe(fileIds) },
          { expand: true }
        )
        .then(res => {
          const messageData = res.data.data;
          // Send the message to the created transaction
          return markTxUnread({
            transactionId: transactionId.uuid,
            recipientId: recipientId.uuid,
            message: messageData,
          }).then(() => {
            dispatch(sendEnquirySuccess());
            dispatch(fetchCurrentUserHasOrdersSuccess(true));
            return response;
          });
        });
    })
    .then(response => {
      if (typeof window === 'object') {
        trackIntercomEvent('New service enquiry', {
          transactionId: response.data.data.id.uuid,
          listingId: listingId.uuid,
          providerId: response.data.data.relationships.provider.data.id.uuid,
          customerId: currentUser.id.uuid,
          transition: 'TRANSITION_ENQUIRE',
          listingType: 'service',
          processName: isLightEntrepreneur ? 'flex-light-process' : 'flex-default-process',
          ...(enquiryPackage
            ? {
                enquiryPackageTitle: enquiryPackage.title,
                enquiryPackagePricingModel: enquiryPackage.pricingModel,
              }
            : {}),
        });
      }
      return response.data.data.id;
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

export const fetchTransactionLineItems = ({ bookingData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ bookingData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        bookingData: bookingData,
      });
    });
};

export const loadData = (params, search) => async (dispatch, getState) => {
  const listingId = new UUID(params.id);
  const searchParams = queryString.parse(search);

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];

  try {
    if (ownListingVariants.includes(params.variant)) {
      await dispatch(showListing(listingId, true));
      // Drafts can't be read through sdk.listings.show, so the other
      // listings are queried by the current user's id.
      if (!getState().user.currentUser) {
        await dispatch(fetchCurrentUser());
      }
      const currentUser = getState().user.currentUser;
      if (currentUser) {
        await dispatch(queryUserListings(listingId, currentUser.id.uuid));
      }
    } else {
      await dispatch(showListing(listingId, false, searchParams));
      await dispatch(queryUserListings(listingId));
      await dispatch(fetchReviews(listingId));
    }
  } catch (error) {
    console.error('Error loading data for ListingPage:', error);
  }
};
